import type { Schemas } from '@npc/api-client'

import { Button } from '../../components/button'
import { Card, CardTitle } from '../../components/card'
import { Tag, type TagTone } from '../../components/tag'
import { T } from '../../copy/text'
import type { CopyId } from '@npc/copy'
import { downgradeAction, useDowngradeNotificationRule } from './downgrade'

/**
 * 「什么时候才吵你」。
 *
 * 每一行的两句话（`title` / `description`）都是**后端渲染好的**，前端一个字不拼。
 * 右边那个 pill 是四个投递档位的人话。
 *
 * ## 那颗「别叫醒我」为什么在这张卡上，不在手机卡上
 *
 * 契约里写着「手机通知卡上那个『以后这种别叫醒我』点下去走这条」
 * （`openapi.yaml` 的 `updateNotificationRule`），而它落不到手机卡上——
 * **`PATCH /notification-rules/{ruleId}` 要一个 rule id，
 * 而 `Alert` 上没有任何字段说得出这条告警归哪条规则。**
 *
 * 从 `severity` 推一个出来是可以写的，但那等于让前端定义
 * 「什么算这一类」——与同屏 `phone.tsx` 里「其余 N 台一切正常」
 * 拒绝自己算的是同一条理由：那是后端的判断。
 *
 * 而且它是可证伪的坏：MSW 夹具的四个 id（`service_down` /
 * `needs_decision` / `handled` / `night_minor`）与服务端那四个
 * （`site_down` / `needs_you` / `handled` / `night_noise`）**对不上**。
 * 任何在前端定死的 id，在 dev 与线上里必有一侧是 404。
 *
 * 规则卡这一侧没有这个问题：id 就是这一行的数据，跟着响应一起来的。
 *
 * 缺的那一半（`Alert` 上一个指向规则的字段）是契约的事，已写进交付报告。
 */
export function Rules({ rules }: { rules: Schemas['NotificationRule'][] }) {
  return (
    <Card>
      <CardTitle className="mb-npc-grid-sm block">
        <T id="alert.rules.heading" />
      </CardTitle>

      <div className="flex flex-col gap-npc-gap-lg">
        {rules.map((rule) => (
          <Rule key={rule.id} rule={rule} />
        ))}
      </div>
    </Card>
  )
}

/**
 * 四个投递档位。
 *
 * **字来自模板**（`alert.channel.*`）——那个域自己写清了理由：
 * 「后端回中文的话同一个值在两屏上迟早出现两种说法，而前端自己写一份是
 * 同一个病换了一侧」。
 *
 * 颜色这一列是前端的：`NotificationRule` 上没有 `tone`，
 * 四条模板上也没有。设计稿给的是打电话红、微信琥珀、另外两档中性，
 * 这里照着摆。**更好的位置是模板上的 `tone` 字段**（`sec.level.*` 就是那么做的），
 * 已提给流 0，落地后这张表直接删掉改读模板。
 */
const CHANNEL = {
  // wave2 P2: 打电话/微信 → ghost/neutral（not coral/danger primary）
  phone: { id: 'alert.channel.phone', tone: 'neutral' },
  push: { id: 'alert.channel.push', tone: 'neutral' },
  digest: { id: 'alert.channel.digest', tone: 'neutral' },
  none: { id: 'alert.channel.none', tone: 'neutral' },
} as const satisfies Record<Schemas['NotificationRule']['channel'], { id: CopyId; tone: TagTone }>

function Rule({ rule }: { rule: Schemas['NotificationRule'] }) {
  const channel = CHANNEL[rule.channel]
  const downgrade = useDowngradeNotificationRule()
  const action = downgradeAction(rule.channel)

  return (
    <div
      data-testid="alert-rule"
      data-rule-id={rule.id}
      className="flex items-center gap-[13px] rounded-npc-inner border border-npc-border-soft px-npc-gap-lg py-[13px]"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-[3px] text-npc-body-sm font-medium">{rule.title}</div>
        <div className="text-npc-caption-sm leading-[1.5] text-npc-ink-soft">
          {rule.description}
        </div>
      </div>

      {/* 档位在 digest 或 none 的行没有这颗按钮——**再降就是关掉**，
          而关掉之后那类问题连早报里都不出现了。见 downgrade.ts 的 quieterThan */}
      {action ? (
        <Button
          size="sm"
          variant="ghost"
          className="flex-none"
          data-testid="alert-rule-quieter"
          disabled={downgrade.isPending}
          onClick={() => downgrade.mutate({ ruleId: rule.id, channel: action.to })}
        >
          <T id={action.copy} />
        </Button>
      ) : null}

      <Tag tone={channel.tone} className="alert-channel-ghost">
        <T id={channel.id} />
      </Tag>
    </div>
  )
}
